import { useQuery } from "@apollo/client";
import { ExternalLink, LayoutGrid } from "lucide-react";
import { useActiveTenant } from "../../../auth/AuthProvider";
import { ALL_BOOKING_TYPES, ALL_PRICING_PLANS } from "./queries";
import type { BookingTypeRecord, PricingPlanRecord } from "./types";

export function CatalogIntro() {
  const tenantDomain = useActiveTenant().domain;
  const btQuery = useQuery<{ allBookingTypes: BookingTypeRecord[] }>(
    ALL_BOOKING_TYPES,
    { fetchPolicy: "cache-only" },
  );
  const ppQuery = useQuery<{ allPricingPlans: PricingPlanRecord[] }>(
    ALL_PRICING_PLANS,
    { fetchPolicy: "cache-only" },
  );

  const services = btQuery.data?.allBookingTypes ?? [];
  const plans = ppQuery.data?.allPricingPlans ?? [];
  const activeServices = services.filter((s) => s.isActive).length;
  const linkedTiers = services.reduce(
    (sum, s) => sum + s.pricingPlans.length,
    0,
  );

  return (
    <div className="min-h-full flex items-center justify-center px-8 py-16">
      <div className="max-w-md text-center">
        <div className="w-12 h-12 mx-auto rounded-lg bg-canvas/60 flex items-center justify-center mb-5">
          <LayoutGrid size={20} className="text-ink-4" strokeWidth={1.5} />
        </div>
        <h2 className="font-display text-[28px] font-medium tracking-tight">
          Catalog
        </h2>
        <p className="text-[13.5px] text-ink-3 mt-2 leading-relaxed">
          Services your clients can book and the price plans that drive them.
          Select something on the left to edit, or use the{" "}
          <span className="font-medium">+</span> buttons to create a new one.
        </p>

        {(services.length > 0 || plans.length > 0) && (
          <div className="grid grid-cols-3 gap-2 mt-6 max-w-sm mx-auto">
            <div className="bg-paper rounded-md hair border px-3 py-2.5">
              <div className="text-[10px] text-ink-4 uppercase tracking-wider">
                Active
              </div>
              <div className="font-display text-[18px] font-medium num mt-0.5">
                {activeServices}
                <span className="text-[11px] text-ink-4">
                  {" / "}
                  {services.length}
                </span>
              </div>
            </div>
            <div className="bg-paper rounded-md hair border px-3 py-2.5">
              <div className="text-[10px] text-ink-4 uppercase tracking-wider">
                Plans
              </div>
              <div className="font-display text-[18px] font-medium num mt-0.5">
                {plans.length}
              </div>
            </div>
            <div className="bg-paper rounded-md hair border px-3 py-2.5">
              <div className="text-[10px] text-ink-4 uppercase tracking-wider">
                Tiers
              </div>
              <div className="font-display text-[18px] font-medium num mt-0.5">
                {linkedTiers}
              </div>
            </div>
          </div>
        )}

        <a
          href={`https://${tenantDomain}/book`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[12px] text-tide hover:text-tide-d inline-flex items-center gap-1.5 mt-6"
        >
          View on site
          <ExternalLink size={11} strokeWidth={2} />
        </a>
      </div>
    </div>
  );
}
